/**
 * Whether a fund hedges its currency exposure.
 *
 * The issuers published this as one column answering four different questions:
 * what currency the units are in, whether hedging happens, where it happens,
 * and whether there is a policy at all. Each row answered some subset, in
 * fifteen distinct strings that varied by issuer even in capitalisation.
 *
 * Only the hedging half lives here. The units' currency comes from the price
 * feed, which knows it for all 127 funds rather than the 29 that happened to
 * mention it — and mentioning it here is what made `USD` ambiguous: on most
 * rows it meant USD units, but on one it meant the *USD exposure* was hedged,
 * which is the opposite kind of statement.
 */
export type HedgeCode = 'u' | 'h' | 'hu' | 'p' | 'd';

export interface HedgeMeta {
    label: string;
    /** Null where the fund states a policy but not an outcome. */
    hedged: boolean | null;
}

export const HEDGING: Record<string, HedgeMeta> = {
    u: { label: 'Unhedged', hedged: false },
    h: { label: 'Hedged', hedged: true },
    // Hedged, but by the fund this one holds rather than by this one — a real
    // distinction seven funds make and worth keeping.
    hu: { label: 'Hedged by the underlying fund', hedged: true },
    p: { label: 'Partially hedged', hedged: null },
    d: { label: "At the manager's discretion", hedged: null },
};

export const hedgeLabel = (code: string | null | undefined): string | null => (code ? (HEDGING[code]?.label ?? null) : null);
export const isHedged = (code: string | null | undefined): boolean | null => (code ? (HEDGING[code]?.hedged ?? null) : null);

/**
 * Read a code out of whatever the issuer wrote.
 *
 * Returns null for a value that names only a currency — "CAD", "USD" — because
 * that states no policy, and recording it as unhedged would be inventing one.
 */
export const codeForHedging = (raw: string | null | undefined): HedgeCode | null => {
    if (!raw) return null;
    const v = raw.trim();
    if (/^(CAD|USD)$/i.test(v)) return null;
    if (/discretion/i.test(v)) return 'd';
    if (/partial/i.test(v)) return 'p';
    if (/hedged\s*\(underlying/i.test(v)) return 'hu';
    if (/unhedged/i.test(v)) return 'u';
    if (/hedged/i.test(v)) return 'h';
    return null;
};
